import React, { useMemo, useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import groupRowStyles from './GroupRow.module.scss';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import EmptyState from '../composites/EmptyState';
import ContentDrill from '../composites/ContentDrill';
import SelectMembersDialog from './SelectMembersDialog';
import GroupRow from './GroupRow';
import ListContent from '../composites/ListContent';
import EditGroup from './EditGroup';
import GroupMembersView from './GroupMembersView';
import InDepthMember from '../Clients/InDepthMember';
import { isHighSchool as _isHighSchool, getFullName } from '../../utils';
import { useErrorContext } from '../../providers/errorProvider';
import {
  useSessionAction,
  useSessionData,
  useUserAction,
} from '../../providers';
import { fetchGroupById, fetchGroupsByGymId, getGymMembers } from '../../api';
import { queryKeys } from '../../api/queryKeys';
import { usePaginatedListQuery } from '../../hooks/usePaginatedListQuery';
import { removeItemFromPaginatedQueries } from '../../api/pagination';

import EditProgram from '../Programs/EditProgram';

import type {
  Group,
  WithOptionalId,
  AssignedProgram,
  Member,
  Program,
} from '../../types';
import { UserType } from '../../constants';
import _ from 'lodash';

const groupListTitles = [
  'Group Name',
  'Members',
  'Created By',
  'Program',
  'Options',
];

const getEmptyGroup = (trainerId: string) => ({
  name: '',
  memberIds: [],
  managerIds: [],
  trainerIds: [trainerId],
});

const Groups: React.FC = () => {
  const {
    loginTrainer,
    gym,
    groups,
    trainers: allGymTrainers,
    members: allGymMembers,
  } = useSessionData();
  const {
    createGroup,
    updateGroup,
    deleteGroup,
    createProgram,
    updateProgramAndRefreshPrograms,
    assignProgramAndRefreshMembers,
  } = useUserAction();
  const { refreshGroup, refreshMembers } = useSessionAction();
  const { setGlobalError } = useErrorContext();
  const queryClient = useQueryClient();

  const [selectedGroup, setSelectedGroup] = useState<string>('');
  const [selectedGroupForDrillDown, setSelectedGroupForDrillDown] =
    useState<Group | null>(null);
  const [isLoadingGroupDetails, setIsLoadingGroupDetails] = useState(false);
  const [selectedMemberForDrillDown, setSelectedMemberForDrillDown] =
    useState<Member | null>(null);
  const [editedGroup, setEditedGroup] = useState<WithOptionalId<Group> | null>(
    null,
  );
  const [editedProgram, setEditedProgram] =
    useState<WithOptionalId<AssignedProgram> | null>(null);
  const [isProgramsDrillInOpen, setIsProgramsDrillInOpen] = useState(false);
  const [isEditGroupDrillInOpen, setIsEditGroupDrillInOpen] = useState(false);
  const [isSelectMembersModalOpen, setIsSelectMembersModalOpen] =
    useState(false);

  const isHighSchool = gym ? _isHighSchool(gym) : false;
  const userType: UserType = isHighSchool ? UserType.Athlete : UserType.Client;

  const groupsListQuery = usePaginatedListQuery<Group>({
  queryKey: (params) => queryKeys.groups.gymPage(gym?.id ?? '', params),
  queryFn: (params) => fetchGroupsByGymId(gym!.id, params),
  enabled: !!gym?.id,
});

  const { data: gymMembersData } = useQuery<Member[]>({
    queryKey: ['members', 'gym-all', gym?.id ?? ''],
    queryFn: async () => {
      const response = await getGymMembers(gym!.id);
      if (!response.success) {
        throw response.error;
      }
      return response.data ?? [];
    },
    enabled: Boolean(gym?.id),
    staleTime: 5 * 60 * 1000,
  });

  const allGroupSelectableMembers = useMemo(() => {
    const merged = new Map<string, Member>();
    Object.values(allGymMembers).forEach((member) => {
      merged.set(member.id, member);
    });
    (gymMembersData ?? []).forEach((member: Member) => {
      merged.set(member.id, {
        ...merged.get(member.id),
        ...member,
      });
    });
    return Array.from(merged.values());
  }, [allGymMembers, gymMembersData]);

  const filteredGroups = useMemo(() => {
    return groupsListQuery.items
      .map((group) => {
        const hydratedGroup = groups[group.id];
        return {
          ...hydratedGroup,
          ...group,
          memberIds: group.memberIds ?? hydratedGroup?.memberIds ?? [],
          trainerIds:
            group.trainerIds ??
            hydratedGroup?.trainerIds ??
            [loginTrainer?.id].filter(Boolean),
          managerIds: group.managerIds ?? hydratedGroup?.managerIds ?? [],
          memberCount:
            group.memberCount ??
            hydratedGroup?.memberCount ??
            hydratedGroup?.memberIds?.length ??
            0,
        };
      })
      .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
  }, [groups, groupsListQuery.items, loginTrainer]);

  const groupsPagination = {
    page: groupsListQuery.page,
    pageSize: groupsListQuery.pageSize,
    totalElements: groupsListQuery.meta.totalElements,
    totalPages: groupsListQuery.meta.totalPages,
    onPageChange: groupsListQuery.setPage,
    onPageSizeChange: groupsListQuery.setPageSize,
    disabled: groupsListQuery.isFetching,
  };

  const invalidateGroupsPage = () => {
    void queryClient.invalidateQueries({ queryKey: ['groups'] });
  };

  // Update selectedGroupForDrillDown when groups data changes
  useEffect(() => {
    if (selectedGroupForDrillDown && groups) {
      const updatedGroup = Object.values(groups).find(
        (group) => group.id === selectedGroupForDrillDown.id,
      );
      if (updatedGroup?.memberIds) {
        setSelectedGroupForDrillDown(updatedGroup);
      }
    }
  }, [groups, selectedGroupForDrillDown]);

  // Keep selected member drill-down in sync with freshest member store data.
  useEffect(() => {
    if (!selectedMemberForDrillDown) {
      return;
    }
    const latestMember = allGymMembers[selectedMemberForDrillDown.id];
    if (!latestMember) {
      setSelectedMemberForDrillDown(null);
      return;
    }
    if (!_.isEqual(latestMember, selectedMemberForDrillDown)) {
      setSelectedMemberForDrillDown(latestMember);
    }
  }, [allGymMembers, selectedMemberForDrillDown]);

  // console.log('filteredGroups', filteredGroups);

  if (!loginTrainer) {
    return (
      <EmptyState
        icon={<GroupsOutlinedIcon />}
        text="No Groups for logged in role."
      />
    );
  }

  const renderEmptyState = () => (
    <EmptyState
      icon={<GroupsOutlinedIcon />}
      cta={{
        icon: <AddBoxOutlinedIcon color="primary" />,
        label: 'Add A Group',
        onClick: () =>
          handleOpenEditGroupDrillIn(getEmptyGroup(loginTrainer.id)),
      }}
      text="No Groups Have Been Added"
    />
  );

  const removeGroup = async (group: Group) => {
    const success = await deleteGroup(group);
    if (!success) {
      return;
    }

    removeItemFromPaginatedQueries<Group>(queryClient, ['groups'], group.id);
    invalidateGroupsPage();
  };

  const loadGroupDetails = async (group: Group): Promise<Group | null> => {
    const { data, success, error } = await fetchGroupById(group.id);
    if (!success || !data) {
      setGlobalError(error?.message || 'Error loading group details');
      return null;
    }
    refreshGroup(data);
    return data;
  };

  const handleEditGroup = async (
    currentGroupData: WithOptionalId<Group>,
    newGroupData: WithOptionalId<Group>,
  ): Promise<boolean> => {
    const success = newGroupData.id
      ? await updateGroup(currentGroupData as Group, newGroupData as Group)
      : await createGroup(
          newGroupData,
          [loginTrainer.id],
          newGroupData.memberIds,
        );
    if (success) {
      invalidateGroupsPage();
    }
    return success;
  };

  const assignProgramToSelectedMembers = async (memberIds: string[]) => {
    if (!editedProgram || !editedGroup) {
      setIsSelectMembersModalOpen(false);
      setEditedProgram(null);
      setEditedGroup(null);
      return;
    }
    const updatedProgram = {
      ...editedProgram,
      startDate: editedProgram.startDate || new Date().toISOString(),
    };
    const updatedGroup = {
      ...editedGroup,
      program: updatedProgram as AssignedProgram,
    };
    await Promise.all([
      assignProgramAndRefreshMembers(
        memberIds,
        updatedProgram as AssignedProgram,
      ),
      handleEditGroup(editedGroup as Group, updatedGroup as Group),
    ]);
    setIsSelectMembersModalOpen(false);
    setEditedProgram(null);
    setEditedGroup(null);
  };

  const handleCloseSelectMembersDialog = () => {
    setEditedProgram(null);
    setEditedGroup(null);
    setIsSelectMembersModalOpen(false);
  };

  const handleAssignProgramFromTemplate = async (
    group: Group,
    program: AssignedProgram,
  ) => {
    await refreshMembers(group.memberIds ?? []);
    setEditedGroup(group);
    setEditedProgram(program);
    setIsSelectMembersModalOpen(true);
  };

  const renderSelectMembersDialog = () => (
    <SelectMembersDialog
      onSubmit={assignProgramToSelectedMembers}
      isOpen={isSelectMembersModalOpen}
      onClose={handleCloseSelectMembersDialog}
      memberIds={editedGroup?.memberIds || []}
    />
  );

  const handleEditGroupProgram = async (group: Group) => {
    const detailedGroup = await loadGroupDetails(group);
    const currentProgram = detailedGroup?.program;
    if (!detailedGroup || !currentProgram?.id) {
      return;
    } else {
      setEditedGroup(detailedGroup);
      setEditedProgram(currentProgram);
      setIsProgramsDrillInOpen(true);
    }
  };

  const handleCreateGroupProgram = async (group: Group) => {
    const detailedGroup = await loadGroupDetails(group);
    if (!detailedGroup) return;
    setEditedGroup(detailedGroup);
    setEditedProgram({
      id: undefined,
      name: '',
      trainerId: loginTrainer.id,
      level: 0,
      fitnessGoal: '',
      programItems: [],
      isPrivate: true,
      startDate: new Date().toISOString(),
    });
    setIsProgramsDrillInOpen(true);
  };

  const handleSubmitEditedGroup = async (
    currentGroupData: WithOptionalId<Group>,
    newGroupData: WithOptionalId<Group>,
  ): Promise<boolean> => {
    const success = await handleEditGroup(currentGroupData, newGroupData);
    return success;
  };

  const handleOpenEditGroupDrillIn = async (
    group: WithOptionalId<Group> | null,
  ) => {
    if (group?.id) {
      const detailedGroup = await loadGroupDetails(group as Group);
      if (!detailedGroup) return;
      setEditedGroup(detailedGroup);
    } else {
      setEditedGroup(group);
    }
    setIsEditGroupDrillInOpen(true);
  };

  const handleGroupSelect = async (group: Group) => {
    setSelectedGroup(group.id);
    setSelectedGroupForDrillDown(group);
    setIsLoadingGroupDetails(true);

    try {
      const detailedGroup = await loadGroupDetails(group);
      if (detailedGroup) {
        const groupMemberIds = detailedGroup.memberIds ?? [];
        const missingMemberIds = groupMemberIds.filter((id) => !allGymMembers[id]);

        if (missingMemberIds.length > 0) {
          await refreshMembers(missingMemberIds);
        }
        setSelectedGroupForDrillDown(detailedGroup);
      }
    } finally {
      setIsLoadingGroupDetails(false);
    }
  };

  const handleGoToInDepthUserPage = (member: Member) => {
    setSelectedMemberForDrillDown(member);
  };

  const renderContent = () => {
    if (
      isEditGroupDrillInOpen ||
      isProgramsDrillInOpen ||
      selectedGroupForDrillDown
    )
      return null;
    return (
      <ListContent
        items={filteredGroups}
        isLoading={groupsListQuery.isLoading}
        listHeaderClassName={groupRowStyles.listGrid}
        pagination={groupsPagination}
        EmptyStateComponent={renderEmptyState}
        titles={groupListTitles}
        ItemComponent={({ data }) => (
          <GroupRow
            group={data}
            deleteGroup={removeGroup}
            allowDelete={loginTrainer.id === data.trainerIds[0]}
            openEditGroupModal={() => handleOpenEditGroupDrillIn(data)}
            selected={selectedGroup === data.id}
            onSelect={() => handleGroupSelect(data)}
            key={data.id}
            onEditProgram={() => handleEditGroupProgram(data)}
            onCreateProgram={() => handleCreateGroupProgram(data)}
            loadGroupDetails={loadGroupDetails}
            onAssignProgramFromTemplate={handleAssignProgramFromTemplate}
          />
        )}
      />
    );
  };

  const handleSubmitEditedProgram = async (
    newProgramData: WithOptionalId<AssignedProgram>,
  ): Promise<boolean> => {
    let programResult: AssignedProgram | null = null;
    let success = false;

    if (!newProgramData.id) {
      const { success: createSuccess, program } = await createProgram(
        newProgramData,
        loginTrainer.id,
      );
      success = createSuccess;
      programResult = program as AssignedProgram | null;
    } else {
      success = await updateProgramAndRefreshPrograms(
        newProgramData as Program,
      );
      programResult = newProgramData as AssignedProgram;
    }

    if (!success || !programResult) {
      return false;
    }

    setEditedProgram({
      ...programResult,
      startDate: newProgramData.startDate ?? programResult.startDate,
    });
    setIsProgramsDrillInOpen(false);
    setIsSelectMembersModalOpen(true);
    return true;
  };

  const handleCloseProgramsDrillIn = () => {
    setIsProgramsDrillInOpen(false);
  };

  const handleCloseEditGroupDrillIn = () => {
    setIsEditGroupDrillInOpen(false);
    setEditedGroup(null);
  };

  const getDrillInContent = () => {
    if (editedProgram && isProgramsDrillInOpen) {
      return (
        <EditProgram
          program={editedProgram}
          onClose={() => handleCloseProgramsDrillIn()}
          onSubmit={handleSubmitEditedProgram}
          proceedAfterSave={false}
        />
      );
    } else if (editedGroup && isEditGroupDrillInOpen) {
      return (
        <EditGroup
          allGymTrainers={Object.values(allGymTrainers)}
          members={allGroupSelectableMembers}
          onSubmit={(newGroupData) =>
            handleSubmitEditedGroup(editedGroup, newGroupData)
          }
          group={editedGroup}
          setGroup={
            setEditedGroup as React.Dispatch<
              React.SetStateAction<WithOptionalId<Group>>
            >
          }
          onClose={() => handleCloseEditGroupDrillIn()}
        />
      );
    } else if (selectedMemberForDrillDown) {
      return <InDepthMember member={selectedMemberForDrillDown} />;
    } else if (selectedGroupForDrillDown) {
      return (
        <GroupMembersView
          group={selectedGroupForDrillDown}
          allGymMembers={allGroupSelectableMembers}
          allGymTrainers={Object.values(allGymTrainers)}
          userType={userType}
          isLoading={isLoadingGroupDetails}
          onGoToInDepthUserPage={handleGoToInDepthUserPage}
          onGroupUpdated={invalidateGroupsPage}
        />
      );
    }

    return null;
  };

  const handleHeaderBack = () => {
    if (selectedMemberForDrillDown) {
      setSelectedMemberForDrillDown(null);
    } else if (selectedGroupForDrillDown) {
      setSelectedGroupForDrillDown(null);
      setIsLoadingGroupDetails(false);
    } else {
      setEditedProgram(null);
      setIsProgramsDrillInOpen(false);
      setEditedGroup(null);
      setIsEditGroupDrillInOpen(false);
    }
  };

  const getSearchPlaceholder = () => {
    if (!filteredGroups.length) return '';
    return 'Search for Groups';
  };

  const getTitle = () => {
    if (selectedMemberForDrillDown) {
      return getFullName(selectedMemberForDrillDown);
    } else if (selectedGroupForDrillDown) {
      return selectedGroupForDrillDown.name;
    } else if (editedGroup && isEditGroupDrillInOpen) {
      return editedGroup.id ? `Edit Group ${editedGroup.name}` : 'Add Group';
    }
    return 'Group';
  };

  const isDrilledIn = Boolean(
    isProgramsDrillInOpen ||
      isEditGroupDrillInOpen ||
      selectedMemberForDrillDown ||
      selectedGroupForDrillDown,
  );

  return (
    <ContentDrill
      title={getTitle()}
      onBack={isDrilledIn ? () => handleHeaderBack() : undefined}
      isParent
      actions={[
        {
          icon: <AddBoxOutlinedIcon />,
          label: 'Add A Group',
          onClick: () =>
            handleOpenEditGroupDrillIn(getEmptyGroup(loginTrainer.id)),
        },
      ]}
      searchPlaceholder={getSearchPlaceholder()}
      searchQuery={groupsListQuery.searchQuery}
      setSearchQuery={groupsListQuery.setSearchQuery}
      disableSearch={false}
      isDrilledIn={isDrilledIn}
      drillInContent={getDrillInContent()}
    >
      {renderContent()}
      {renderSelectMembersDialog()}
    </ContentDrill>
  );
};

export default Groups;
